import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import {
  CompanyHubSubscribe200Response,
  GlobalHubSubscribe200Response,
} from '../connectors/realtime';

const onRtNotification = <
  THub extends CompanyHubSubscribe200Response | GlobalHubSubscribe200Response,
  T extends THub['eventName'],
>(
  hub: HubConnection,
  eventName: T,
  callback: (payload: (THub & { eventName: T })['payload']) => void,
) => hub.on(eventName, callback);

const hubCacheKey = (companyId?: string) => companyId ?? '__global__';

const hubCache = new Map<string, Promise<HubConnection>>();

const connectSignalrHub = async (
  companyId: string | undefined,
  onClose: () => void,
): Promise<HubConnection> => {
  const hub = new HubConnectionBuilder()
    .withUrl(
      companyId
        ? `${environment.baseApiPath}/rt/companies/${companyId}`
        : `${environment.baseApiPath}/rt/global`,
    )
    .configureLogging(LogLevel.Information)
    .build();

  hub.onclose(onClose);

  await hub.start();
  await hub.invoke('Subscribe');

  return hub;
};

export const createSignalrHub = async (companyId?: string) => {
  const key = hubCacheKey(companyId);
  const cached = hubCache.get(key);
  if (cached) {
    const hub = await cached;
    if (hub.state === HubConnectionState.Connected) {
      return hub;
    }
    if (hubCache.get(key) === cached) {
      hubCache.delete(key);
    }
  }

  // Evict only our own entry: by the time onclose fires (or start fails), the
  // cache may already hold a newer connection for the same key.
  const evict = () => {
    if (hubCache.get(key) === connectionPromise) {
      hubCache.delete(key);
    }
  };
  const connectionPromise = connectSignalrHub(companyId, evict);
  hubCache.set(key, connectionPromise);

  try {
    return await connectionPromise;
  } catch (error) {
    evict();
    throw error;
  }
};

/**
 * `HubConnection.onclose` has no unregister API, so each hub gets a single
 * dispatcher and waiters subscribe through this set.
 */
const hubCloseWaiters = new WeakMap<
  HubConnection,
  Set<(error?: Error) => void>
>();

const onHubClose = (
  hub: HubConnection,
  callback: (error?: Error) => void,
): (() => void) => {
  let waiters = hubCloseWaiters.get(hub);
  if (!waiters) {
    const created = new Set<(error?: Error) => void>();
    waiters = created;
    hubCloseWaiters.set(hub, created);
    hub.onclose((error) => {
      const current = [...created];
      created.clear();
      for (const waiter of current) {
        waiter(error);
      }
    });
  }
  const set = waiters;
  set.add(callback);
  return () => set.delete(callback);
};

export const waitForFirstNotification = async <
  THub extends CompanyHubSubscribe200Response | GlobalHubSubscribe200Response,
  T extends THub['eventName'],
  TPayload extends (THub & {
    eventName: T;
  })['payload'],
>(
  hub: HubConnection,
  eventName: T,
  options?: {
    check?: (payload: TPayload) => boolean;
    timeoutMs?: number;
  },
): Promise<TPayload> => {
  return new Promise((resolve, reject) => {
    if (hub.state !== HubConnectionState.Connected) {
      reject(new Error(`Hub is not connected while waiting for ${eventName}`));
      return;
    }

    const cleanup = () => {
      clearTimeout(timeout);
      hub.off(eventName, handler);
      offClose();
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for ${eventName}`));
    }, options?.timeoutMs ?? 60_000);

    const handler = (payload: unknown) => {
      if (!options?.check || options.check(payload as TPayload)) {
        cleanup();
        resolve(payload as TPayload);
      }
    };

    const offClose = onHubClose(hub, (error) => {
      cleanup();
      reject(
        error ?? new Error(`Connection closed while waiting for ${eventName}`),
      );
    });

    onRtNotification(hub, eventName, handler);
  });
};
